import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Search, Filter, MapPin, Car, User } from 'lucide-react';
import {
  AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir,
  BotaoIniciar, BotaoFinalizar, BotaoCancelar,
} from '../../components/BotoesAcao';
import { viagensService, ViagensFilters } from '../../services/viagens';
import ExportButtons from '../../components/ExportButtons';
import { useConfirm, usePrompt } from '../../components/ConfirmDialog';
import { exportService } from '../../services/export';
import { SeloDeDesvio } from '../../components/DesvioDaRota';
import { formatCurrency, formatDate, formatKilometers } from '../../utils/formatters';
import { lugarEmUmaLinha } from '../../utils/lugares';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';

const ViagensList: React.FC = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const showPrompt = usePrompt();
  
  const [filters, setFilters] = useState<ViagensFilters>({
    per_page: 15,
    sort_by: 'data_saida',
    sort_order: 'desc'
  });

  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['viagens', filters],
    queryFn: () => viagensService.getViagens(filters),
    placeholderData: (previousData) => previousData
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => viagensService.deleteViagem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      toast.success('Viagem excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir viagem');
    }
  });

  const iniciarMutation = useMutation({
    mutationFn: (id: number) => viagensService.iniciarViagem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      toast.success('Viagem iniciada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar viagem');
    }
  });

  const finalizarMutation = useMutation({
    mutationFn: ({ id, kmChegada }: { id: number; kmChegada: number }) => 
      viagensService.finalizarViagem(id, { km_chegada: kmChegada }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      toast.success('Viagem finalizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar viagem');
    }
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: number) => viagensService.cancelarViagem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      toast.success('Viagem cancelada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao cancelar viagem');
    }
  });

  const handleFilterChange = (key: keyof ViagensFilters, value: any) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'page' ? {} : { page: 1 }),
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      busca: searchTerm || undefined,
      page: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      per_page: 15,
      sort_by: 'data_saida',
      sort_order: 'desc'
    });
    setSearchTerm('');
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Excluir viagem',
      message: 'Tem certeza que deseja excluir esta viagem? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (ok) {
      deleteMutation.mutate(id);
    }
  };

  const handleIniciar = async (id: number) => {
    const ok = await confirm({
      title: 'Iniciar viagem',
      message: 'Deseja iniciar esta viagem?',
      confirmLabel: 'Iniciar',
      variant: 'primary',
    });
    if (ok) {
      iniciarMutation.mutate(id);
    }
  };

  const handleFinalizar = async (id: number) => {
    const kmChegada = await showPrompt({
      title: 'Finalizar viagem',
      message: 'Informe a quilometragem de chegada:',
      confirmLabel: 'Finalizar',
      variant: 'primary',
      input: { type: 'number', label: 'Km de chegada', placeholder: 'Ex: 105000', min: 0, required: true },
    });
    if (kmChegada && !isNaN(Number(kmChegada))) {
      finalizarMutation.mutate({ id, kmChegada: Number(kmChegada) });
    }
  };

  const handleCancelar = async (id: number) => {
    const ok = await confirm({
      title: 'Cancelar viagem',
      message: 'Deseja cancelar esta viagem?',
      confirmLabel: 'Cancelar viagem',
      cancelLabel: 'Voltar',
      variant: 'danger',
    });
    if (ok) {
      cancelarMutation.mutate(id);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      concluida: 'bg-green-100 text-green-800',
      em_andamento: 'bg-blue-100 text-blue-800',
      planejada: 'bg-yellow-100 text-yellow-800',
      cancelada: 'bg-red-100 text-red-800'
    } as const;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTipoBadge = (tipo: string) => {
    const colors = {
      comercial: 'bg-blue-100 text-blue-800',
      particular: 'bg-green-100 text-green-800',
      manutencao: 'bg-orange-100 text-orange-800',
      emergencia: 'bg-red-100 text-red-800',
      outros: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[tipo as keyof typeof colors] || colors.outros}`}>
        {tipo.toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    // Falta de permissão não é falha: recarregar não resolve, e o usuário
    // precisa saber que o caminho é pedir o acesso a quem administra.
    if (ehSemPermissao(error)) {
      return <AvisoSemPermissao erro={error} />;
    }

    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{mensagemDoErro(error, 'Erro ao carregar viagens')}</p>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const viagens = data?.data || [];
  // A API devolve os campos de paginacao dentro de `pagination`, nao na raiz.
  // Ler da raiz deixava last_page indefinido e o rodape nunca aparecia.
  const pagination = data?.pagination ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Viagens</h1>
          <p className="text-gray-600">Controle de viagens e rotas</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.viagensPdf()}
            onExportExcel={() => exportService.viagensExcel()}
          />
          )}
          <button
            onClick={() => navigate('/viagens/novo')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova viagem
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="planejada">Planejada</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="concluida">Concluída</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => handleFilterChange('tipo', e.target.value || undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="comercial">Comercial</option>
                <option value="particular">Particular</option>
                <option value="manutencao">Manutenção</option>
                <option value="emergencia">Emergência</option>
                <option value="outros">Outros</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="date"
                value={filters.data_inicio || ''}
                onChange={(e) => handleFilterChange('data_inicio', e.target.value || undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="date"
                value={filters.data_fim || ''}
                onChange={(e) => handleFilterChange('data_fim', e.target.value || undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por origem, parada ou destino..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </button>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="w-4 h-4 mr-2" />
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Viagens */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {viagens.length === 0 ? (
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma viagem encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Viagem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veículo / Motorista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rota
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status / Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distância / Custo
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {viagens.map((viagem) => (
                    <tr key={viagem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {viagem.finalidade}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(viagem.data_saida)}
                          </div>
                          {viagem.data_chegada && (
                            <div className="text-sm text-gray-500">
                              Chegada: {formatDate(viagem.data_chegada)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Car className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {viagem.veiculo?.placa}
                            </div>
                            <div className="text-sm text-gray-500">
                              {viagem.veiculo?.marca} {viagem.veiculo?.modelo}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center mt-1">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-500">
                            {viagem.motorista?.nome}
                          </div>
                        </div>
                      </td>
                      {/* Largura travada e corte por linha: o endereço do mapa
                          chega com ~90 caracteres e, sem isso, quebra em quatro
                          linhas e espreme a coluna inteira. O texto completo
                          fica no `title` e no detalhe da viagem. */}
                      <td className="px-6 py-4 max-w-[16rem]">
                        <div className="truncate text-sm text-gray-900" title={viagem.origem}>
                          {lugarEmUmaLinha(viagem.origem)}
                        </div>
                        <div className="truncate text-sm text-gray-500" title={viagem.destino}>
                          <span className="text-gray-400">↓</span> {lugarEmUmaLinha(viagem.destino)}
                        </div>
                        {!!viagem.paradas?.length && (
                          <div
                            className="truncate text-xs text-gray-400"
                            title={viagem.paradas.map((p) => p.endereco).join(' → ')}
                          >
                            +{viagem.paradas.length} {viagem.paradas.length === 1 ? 'parada' : 'paradas'} no caminho
                          </div>
                        )}
                        {viagem.rota && (
                          <div className="truncate text-xs text-gray-400" title={viagem.rota}>
                            {viagem.rota}
                          </div>
                        )}
                        {/* A rota prevista fica aqui, e não na coluna de
                            distância: ali o número seria confundido com o
                            rodado, que é de outra natureza. */}
                        {viagem.distancia_prevista_km != null && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Rota do mapa: {formatKilometers(viagem.distancia_prevista_km)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getStatusBadge(viagem.status)}
                          {getTipoBadge(viagem.tipo)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          {viagem.km_percorridos && (
                            <div className="text-sm text-gray-900 flex items-center gap-2">
                              {formatKilometers(viagem.km_percorridos)}
                              {/* O selo so aparece quando ha rota tracada para
                                  comparar. Numero solto nao diz se foi muito. */}
                              <SeloDeDesvio desvio={viagem.desvio_da_rota} />
                            </div>
                          )}
                          {viagem.valor_total && (
                            <div className="text-sm text-gray-500">
                              {formatCurrency(viagem.valor_total)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <AcoesDaLinha>
                          <BotaoVer onClick={() => navigate(`/viagens/${viagem.id}`)} title="Ver detalhes" />
                          {pode('viagens', 'editar') && <BotaoEditar onClick={() => navigate(`/viagens/${viagem.id}/editar`)} />}
                          {viagem.status === 'planejada' && pode('viagens', 'iniciar') && (
                            <BotaoIniciar
                              onClick={() => handleIniciar(viagem.id)}
                              disabled={iniciarMutation.isPending}
                              title="Iniciar viagem"
                            />
                          )}
                          {viagem.status === 'em_andamento' && pode('viagens', 'finalizar') && (
                            <BotaoFinalizar
                              onClick={() => handleFinalizar(viagem.id)}
                              disabled={finalizarMutation.isPending}
                              title="Finalizar viagem"
                            />
                          )}
                          {(viagem.status === 'planejada' || viagem.status === 'em_andamento') && pode('viagens', 'cancelar') && (
                            <BotaoCancelar
                              onClick={() => handleCancelar(viagem.id)}
                              disabled={cancelarMutation.isPending}
                              title="Cancelar viagem"
                            />
                          )}
                          {pode('viagens', 'excluir') && <BotaoExcluir onClick={() => handleDelete(viagem.id)} disabled={deleteMutation.isPending} />}
                        </AcoesDaLinha>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination && pagination.last_page > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                      </span> de{' '}
                      <span className="font-medium">{pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ViagensList;
